import { computed, inject, Injectable, Signal } from '@angular/core';
import { httpResource, HttpResourceRef, HttpResourceRequest } from '@angular/common/http';

import { LanguageService } from '@core/i18n/language.service';
import { environment } from '@env/environment';
import {
    groupSectionsByBlockId,
    LandingGraphqlResponse,
    LandingSectionGroup,
    LandingSeo,
    TymeGroupLanding,
} from '@core/cms/landing.cms';
import { TYME_GROUP_LANDING_QUERY } from '@core/cms/tyme-group-landing.query';

/**
 * Single source of truth for the Tyme Group landing CMS payload. Owns the language-reactive
 * `httpResource` (previously inlined in {@link App}) so both the root shell (debug logging) and the
 * home page (dynamic section rendering) read the same fetch instead of issuing it twice.
 */
@Injectable({ providedIn: 'root' })
export class LandingCmsService {
    private readonly _language: LanguageService = inject(LanguageService);
    private readonly _cmsUrl: string = environment.cmsUrl;

    /** Active language code; drives the CMS request locale. */
    private readonly _lang: Signal<string> = this._language.lang;

    /**
     * Tyme Group landing content from the CMS. Reactive: refetches when the language changes, runs
     * during SSR with the result transferred to the client, and exposes `landing.value()`,
     * `landing.isLoading()` and `landing.error()` for consumers.
     */
    public readonly landing: HttpResourceRef<TymeGroupLanding | null> =
        httpResource<TymeGroupLanding | null>(
            (): HttpResourceRequest | undefined =>
                this._cmsUrl
                    ? {
                          url: `${this._cmsUrl}/graphql`,
                          method: 'POST',
                          headers: { 'accept-language': this._lang() },
                          body: {
                              operationName: 'TymeGroupLanding',
                              query: TYME_GROUP_LANDING_QUERY,
                              variables: { locale: this._lang(), status: 'PUBLISHED' },
                          },
                      }
                    : undefined,
            {
                defaultValue: null,
                parse: (raw: unknown): TymeGroupLanding | null =>
                    (raw as LandingGraphqlResponse)?.data?.tymeGroupLanding ?? null,
            },
        );

    /**
     * Landing sections grouped into runs of consecutive entries sharing the same `blockId`
     * (see {@link groupSectionsByBlockId}). Drives the dynamic CMS renderer on the home page.
     */
    public readonly sectionGroups: Signal<ReadonlyArray<LandingSectionGroup>> = computed(
        (): ReadonlyArray<LandingSectionGroup> =>
            groupSectionsByBlockId(this.landing.value()?.Sections ?? []),
    );

    /** The landing page's `shared.seo` component for the active language (`null` until resolved). */
    public readonly seo: Signal<LandingSeo | null> = computed(
        (): LandingSeo | null => this.landing.value()?.seo ?? null,
    );
}
